/**
 * Drill grading — dispatches each archetype to an engine primitive, so grading is
 * identical in the browser and the Worker. Returns the engine's GradeResult; the
 * feedback engine turns that into a FeedbackEvent.
 */

import {
  gradeSingleSelect,
  gradeSet,
  gradeFill,
  type GradeResult,
  type MisconceptionTag,
} from '@usacow/engines';
import type { DrillContent } from './content.js';

export type DrillResponse = string | Record<string, string>;

const withMisconception = (g: GradeResult, tag?: string): GradeResult =>
  g.status === 'correct' || !tag
    ? g
    : { ...g, misconceptionTags: [tag as MisconceptionTag, ...g.misconceptionTags] };

export function gradeDrill(content: DrillContent, response: DrillResponse): GradeResult {
  switch (content.archetype) {
    case 'select':
      return gradeSingleSelect({
        correctId: content.correctId,
        acceptedIds: content.acceptedIds,
        responseId: typeof response === 'string' ? response : '',
        distractors: content.distractors,
      });

    case 'tag': {
      const resp = (typeof response === 'object' ? response : {}) as Record<string, string>;
      return gradeSet({
        correctIds: content.spans.map((s) => `${s.id}:${s.gold}`),
        responseIds: content.spans.map((s) => `${s.id}:${resp[s.id] ?? ''}`),
      });
    }

    case 'predict': {
      const g = gradeFill({
        expected: [content.expected],
        responses: [typeof response === 'string' ? response : ''],
      });
      return withMisconception(g, content.misconceptionOnFail);
    }

    case 'fill': {
      const g = gradeFill({
        expected: [content.expected],
        responses: [typeof response === 'string' ? response : ''],
      });
      return withMisconception(g, content.bugTags[0]);
    }
  }
}
